/**
 * API Client Types
 *
 * Common types and interfaces for the API client
 */

import type { AxiosRequestConfig, AxiosResponse } from 'axios';

/**
 * API Client Configuration
 */
export interface ApiClientConfig {
  baseURL?: string;
  timeout?: number;
  headers?: Record<string, string>;
  withCredentials?: boolean;
}

/**
 * API Response wrapper
 */
export interface ApiResponse<T = unknown> {
  data: T;
  status: number;
  statusText: string;
  headers: Record<string, string>;
}

/**
 * API Error
 */
export interface ApiError {
  message: string;
  status?: number;
  statusText?: string;
  data?: unknown;
  code?: string;
  isApiError: true;
}

/**
 * Pagination parameters
 */
export interface PaginationParams {
  page?: number;
  limit?: number;
  offset?: number;
}

/**
 * Sorting parameters
 */
export interface SortParams {
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

/**
 * Filter parameters for investments
 */
export interface InvestmentFilterParams extends PaginationParams, SortParams {
  status?: string;
  portco_id?: number;
  segment_id?: number;
  is_regulatory_requirement?: boolean;
  submitted_from?: string;
  submitted_to?: string;
  search?: string;
  tag_ids?: string; // Comma-separated list of tag IDs
  research_owner_email?: string; // Filter by research owner email
}

/**
 * Common query parameters
 */
export interface QueryParams extends PaginationParams, SortParams {
  [key: string]: string | number | boolean | undefined;
}

/**
 * Request configuration
 */
export interface RequestConfig extends AxiosRequestConfig {
  retry?: boolean;
  retryCount?: number;
}

/**
 * Pagination metadata returned from API
 */
export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

/**
 * Paginated response from API
 */
export interface PaginatedResponse<T> {
  data: T[];
  pagination: PaginationMeta;
}

/**
 * Interceptor types
 */
export type RequestInterceptor = (
  config: AxiosRequestConfig
) => AxiosRequestConfig | Promise<AxiosRequestConfig>;
export type ResponseInterceptor = (
  response: AxiosResponse
) => AxiosResponse | Promise<AxiosResponse>;
export type ErrorInterceptor = (error: unknown) => unknown;

/**
 * API success response wrapper shape from the backend
 */
export interface ApiSuccessWrapper<T = unknown> {
  success: boolean;
  data: T;
  meta?: Record<string, unknown>;
}

/**
 * Unwrap API response data from the success wrapper.
 * The API returns { success, data, meta } - this extracts the inner data.
 */
export function unwrapResponse<T>(responseData: unknown): T {
  if (
    responseData !== null &&
    typeof responseData === 'object' &&
    'success' in (responseData as Record<string, unknown>) &&
    'data' in (responseData as Record<string, unknown>)
  ) {
    return (responseData as ApiSuccessWrapper<T>).data;
  }
  return responseData as T;
}
